// AuthContext.js with enhanced token management
import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup,
  deleteUser,
  sendEmailVerification,
  applyActionCode,
  verifyBeforeUpdateEmail,
  getIdToken // Add this import for explicit token management
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp, deleteDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import PendingOrderHandler from '../utils/pendingOrderHandler';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingOrder, setPendingOrder] = useState(null);
  const [tokenRefreshTimestamp, setTokenRefreshTimestamp] = useState(null);

  // Enhanced token retrieval method
  async function refreshUserToken(user) {
    try {
      if (user) {
        const token = await getIdToken(user, true);
        console.log('Token Refresh Details:', {
          uid: user.uid,
          tokenLength: token ? token.length : 'No Token',
          timestamp: new Date().toISOString()
        });
        setTokenRefreshTimestamp(Date.now());
        return token;
      }
    } catch (error) {
      console.error('Token Refresh Error:', {
        message: error.message,
        code: error.code
      });
    }
  }

  async function signup(email, password, name) {
    try {
      // Validate inputs
      if (!email || !password || !name) {
        throw new Error('All fields are required');
      }

      // Create the user in Firebase Authentication
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      console.log('User created successfully:', user.uid);
      
      // Update user profile with display name
      await updateProfile(user, { 
        displayName: name 
      });
      
      // Refresh token immediately after signup
      await refreshUserToken(user);
      
      // Create a user profile document in Firestore
      const userDocRef = doc(db, "users", user.uid);
      await setDoc(userDocRef, {
        name,
        email,
        createdAt: serverTimestamp(),
        isAdmin: false,
        uid: user.uid,
        emailVerified: false // Track verification status in Firestore
      }, { merge: true });

      console.log('User document created successfully');

      // Send verification email
      await sendEmailVerification(user);
      console.log('Verification email sent');

      // Check for and restore pending order
      const savedPendingOrder = await PendingOrderHandler.getPendingOrder(user.uid);
      if (savedPendingOrder) {
        setPendingOrder(savedPendingOrder);
        window.dispatchEvent(new CustomEvent('restore-pending-order', { 
          detail: savedPendingOrder 
        }));
      }

      return userCredential;
    } catch (error) {
      console.error('Signup error:', error);
      
      // More detailed error logging
      console.error('Error Code:', error.code);
      console.error('Error Message:', error.message);

      // Throw a more user-friendly error
      switch (error.code) {
        case 'auth/email-already-in-use':
          throw new Error('Email is already registered. Please log in or use a different email.');
        case 'auth/invalid-email':
          throw new Error('Invalid email address. Please check and try again.');
        case 'auth/weak-password':
          throw new Error('Password is too weak. Please use a stronger password.');
        default:
          throw new Error('Failed to create account. Please try again.');
      }
    }
  }

  // Existing methods remain the same, with token refresh added where appropriate
  async function login(email, password) {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Refresh token immediately after login
      await refreshUserToken(user);

      // Update email verification status in Firestore
      if (user.emailVerified) {
        const userDocRef = doc(db, "users", user.uid);
        await setDoc(userDocRef, {
          emailVerified: true,
          lastLogin: serverTimestamp()
        }, { merge: true });
      }

      // Check for and restore pending order
      const savedPendingOrder = await PendingOrderHandler.getPendingOrder(user.uid);
      if (savedPendingOrder) {
        setPendingOrder(savedPendingOrder);
        window.dispatchEvent(new CustomEvent('restore-pending-order', { 
          detail: savedPendingOrder 
        }));
      }

      return user;
    } catch (error) {
      console.error('Login error:', error);
      
      // More specific error handling
      switch (error.code) {
        case 'auth/user-not-found':
          throw new Error('No account found with this email. Please sign up.');
        case 'auth/wrong-password':
          throw new Error('Incorrect password. Please try again.');
        case 'auth/invalid-email':
          throw new Error('Invalid email address. Please check and try again.');
        default:
          throw new Error('Login failed. Please try again.');
      }
    }
  }

  async function signInWithGoogle() {
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // Refresh token immediately after Google sign-in
      await refreshUserToken(user);

      // Create or update user document in Firestore
      const userDocRef = doc(db, "users", user.uid);
      await setDoc(userDocRef, {
        name: user.displayName,
        email: user.email,
        photoURL: user.photoURL,
        createdAt: serverTimestamp(),
        lastLogin: serverTimestamp(),
        isAdmin: false,
        uid: user.uid,
        emailVerified: user.emailVerified // Google accounts come pre-verified
      }, { merge: true });

      console.log('Google sign-in successful:', user.uid);

      // Check for and restore pending order
      const savedPendingOrder = await PendingOrderHandler.getPendingOrder(user.uid);
      if (savedPendingOrder) {
        setPendingOrder(savedPendingOrder);
        window.dispatchEvent(new CustomEvent('restore-pending-order', { 
          detail: savedPendingOrder 
        }));
      }

      return user;
    } catch (error) {
      console.error('Google sign-in error:', error);
      console.error('Error Code:', error.code);
      console.error('Error Message:', error.message);

      // Handle specific Google sign-in errors
      switch (error.code) {
        case 'auth/popup-closed-by-user':
          throw new Error('Sign-in was cancelled. Please try again.');
        case 'auth/popup-blocked':
          throw new Error('Sign-in popup was blocked. Please allow popups for this site and try again.');
        case 'auth/account-exists-with-different-credential':
          throw new Error('An account already exists with the same email address but different sign-in credentials. Please sign in using the original method.');
        default:
          throw new Error('Failed to sign in with Google. Please try again.');
      }
    }
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setLoading(true);
      
      if (user) {
        // Refresh token on auth state change
        await refreshUserToken(user);
        
        setCurrentUser(user);

        console.log('Current User Details:', {
          uid: user?.uid,
          email: user?.email,
          emailVerified: user?.emailVerified
        });
        
        try {
          const userProfile = await fetchUserProfile(user.uid);

          // Check if email verification status has changed and update Firestore
          if (user.emailVerified && userProfile && !userProfile.emailVerified) {
            const userDocRef = doc(db, "users", user.uid);
            await setDoc(userDocRef, {
              emailVerified: true
            }, { merge: true });
            console.log('Email verification status updated in Firestore');
          }

          // Check for pending order on auth state change
          const savedPendingOrder = await PendingOrderHandler.getPendingOrder(user.uid);
          if (savedPendingOrder) {
            setPendingOrder(savedPendingOrder);
            window.dispatchEvent(new CustomEvent('restore-pending-order', { 
              detail: savedPendingOrder 
            }));
          }
        } catch (error) {
          console.error('Error during auth state change:', error);
        }
      } else {
        setCurrentUser(null);
        setUserProfile(null);
        setPendingOrder(null);
      }
      
      setLoading(false);
    }, (error) => {
      console.error('Auth state change error:', error);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Rest of the existing methods remain unchanged
  function logout() {
    return signOut(auth);
  }

  function resetPassword(email) {
    return sendPasswordResetEmail(auth, email);
  }

  function sendVerificationEmail() {
    if (!currentUser) throw new Error('No user logged in');
    return sendEmailVerification(currentUser);
  }

  async function verifyEmail(actionCode) {
    console.log('AuthContext: verifyEmail called with code length:', actionCode ? actionCode.length : 0);
    
    try {
      await applyActionCode(auth, actionCode);
      console.log('AuthContext: Email verification successful');
      
      if (currentUser) {
        await currentUser.reload();
        setCurrentUser(auth.currentUser);
        
        const userDocRef = doc(db, "users", currentUser.uid);
        await setDoc(userDocRef, {
          emailVerified: true
        }, { merge: true });
        console.log('AuthContext: User profile updated after verification');
      }
      
      return true;
    } catch (error) {
      console.error('AuthContext: Email verification error:', error);
      console.error('AuthContext: Error code:', error.code);
      console.error('AuthContext: Error message:', error.message);
      
      throw error;
    }
  }

  function updateEmail(newEmail) {
    if (!currentUser) throw new Error('No user logged in');
    return verifyBeforeUpdateEmail(currentUser, newEmail);
  }

  async function fetchUserProfile(userId) {
    try {
      const userDocRef = doc(db, "users", userId);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        const profileData = userDoc.data();
        setUserProfile(profileData);
        return profileData;
      } else {
        console.log("No user profile found!");
        return null;
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
      throw error;
    }
  }

  async function deleteUserAccount() {
    if (!currentUser) throw new Error('No user logged in');
    
    try {
      const userDocRef = doc(db, "users", currentUser.uid);
      
      await deleteDoc(userDocRef);
      await deleteUser(currentUser);
      await signOut(auth);
      
      return true;
    } catch (error) {
      console.error("Error deleting account:", error);
      throw error;
    }
  }

  const value = {
    currentUser,
    userProfile,
    pendingOrder,
    tokenRefreshTimestamp,
    signup,
    login,
    logout,
    resetPassword,
    fetchUserProfile,
    signInWithGoogle,
    deleteUserAccount,
    sendVerificationEmail,
    verifyEmail,
    updateEmail,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthContext;